import { useTranslation } from 'react-i18next';
import type { WesteerModule, User } from '../../auth';
import type { BaseFormSectionProps } from '../types/project-form-types';

/**
 * Todos los módulos OPCIONALES disponibles en WESTEER
 *
 * @remarks
 * IMPORTANTE: Esta lista debe coincidir con VALID_MODULES del backend
 * Ver: packages/core/backend/src/projects/dto/create-project.dto.ts:46-56
 *
 * NOTA: 'urban-network' NO está en esta lista porque es OBLIGATORIO (core del sistema).
 * Se incluye automáticamente en todos los proyectos, no es seleccionable.
 */
const OPTIONAL_MODULES: WesteerModule[] = [
  'roadways',      // Vialidades - gestión de red vial, segmentos y defectos
  'infrastructure',
  'services',
  'transit',
  'transport',
  'waste',
  'safety',
  'urban',
  'environmental',
  'social',
  // 'urban-network' NO está aquí - es CORE y siempre está habilitado
];

/**
 * Obtiene la clave de traducción para un módulo
 *
 * @param module - Módulo de WESTEER
 * @returns Clave de traducción i18n
 */
const getModuleTranslationKey = (module: WesteerModule): string => {
  const keys: Record<WesteerModule, string> = {
    mobility: 'form.moduleMobility',
    environmental: 'form.moduleEnvironmental',
    infrastructure: 'form.moduleInfrastructure',
    urban: 'form.moduleUrban',
    social: 'form.moduleSocial',
    services: 'form.moduleServices',
    safety: 'form.moduleSafety',
    waste: 'form.moduleWaste',
    vias: 'form.moduleVias',
    'urban-network': 'form.moduleUrbanNetwork', // Red Urbana Canónica (CORE)
    // Legacy modules (deprecated - should not appear in new projects)
    transit: 'form.moduleTransit',
    transport: 'form.moduleTransport',
    roadways: 'form.moduleRoadways',
  };
  return keys[module] || module;
};

/**
 * Props extendidas para ProjectContractDetails
 */
interface ProjectContractDetailsProps extends BaseFormSectionProps {
  availableUsers: User[];
  isLoadingUsers: boolean;
}

/**
 * ProjectContractDetails - Detalles contractuales del proyecto
 *
 * @remarks
 * Incluye:
 * - Administrador del proyecto
 * - Período de vigencia del servicio (startDate, endDate)
 * - Módulos contratados (pago por módulo)
 *
 * **Cumple con:**
 * - P1: Componente < 200 líneas
 * - P3: Documentación JSDoc completa
 */
export function ProjectContractDetails({
  formData,
  isLoading,
  availableUsers,
  isLoadingUsers,
  onFormDataChange,
}: ProjectContractDetailsProps): JSX.Element {
  const { t } = useTranslation('projects');

  /**
   * Maneja el toggle de un módulo
   */
  const handleModuleToggle = (module: WesteerModule): void => {
    const isCurrentlyActive = formData.activeModules.includes(module);
    const newModules = isCurrentlyActive
      ? formData.activeModules.filter((m) => m !== module)
      : [...formData.activeModules, module];

    onFormDataChange({ activeModules: newModules });
  };

  return (
    <div className="form-section">
      <h2 className="form-section-title-contract">{t('form.serviceDetails')}</h2>

      {/* Administrador del Proyecto */}
      <div className="form-group">
        <label htmlFor="projectAdminId">{t('form.projectAdministrator')}</label>
        <select
          id="projectAdminId"
          value={formData.projectAdminId}
          onChange={(e) => onFormDataChange({ projectAdminId: e.target.value })}
          disabled={isLoading || isLoadingUsers || !formData.tenantId}
          className={`form-input ${formData.projectAdminId ? 'has-value' : ''}`}
        >
          <option value="">{t('form.selectProjectAdmin')}</option>
          {availableUsers.map((user) => (
            <option key={user.id} value={user.id}>
              {user.firstName} {user.lastName} ({user.email})
            </option>
          ))}
        </select>
        <small className="form-help">{t('form.projectAdminHelp')}</small>
      </div>

      {/* Vigencia */}
      <div className="form-group">
        <label htmlFor="startDate">{t('form.servicePeriod')}</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <div>
            <input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => onFormDataChange({ startDate: e.target.value })}
              disabled={isLoading}
              className={`form-input ${formData.startDate ? 'has-value' : ''}`}
              placeholder={t('form.startDate')}
            />
          </div>
          <div>
            <input
              id="endDate"
              type="date"
              value={formData.endDate}
              onChange={(e) => onFormDataChange({ endDate: e.target.value })}
              disabled={isLoading}
              className={`form-input ${formData.endDate ? 'has-value' : ''}`}
              placeholder={t('form.endDate')}
            />
          </div>
        </div>
      </div>

      {/* Módulos contratados */}
      <div className="form-group">
        <label>{t('form.contractedModules')}</label>
        <div className="modules-checklist">
          {OPTIONAL_MODULES.map((module) => {
            const isActive = formData.activeModules.includes(module);

            return (
              <div key={module} className="module-checkbox-item">
                <label>
                  <input
                    type="checkbox"
                    checked={isActive}
                    disabled={isLoading}
                    onChange={() => handleModuleToggle(module)}
                  />
                  <span className="module-name">{t(getModuleTranslationKey(module))}</span>
                </label>
              </div>
            );
          })}
        </div>
        <small className="form-help">
          {t('form.modulesHelpPayPerModule')}
          <br />
          <strong>Nota:</strong> Red Urbana Canónica está siempre habilitada (módulo core del sistema).
        </small>
      </div>
    </div>
  );
}
